import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Target, Plus, CheckCircle, Calendar, Trash2, Edit2,
  ArrowLeft, Sparkles, Trophy, Eye, EyeOff, Users, Lock,
  CalendarPlus, ExternalLink, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import VoiceInput from '@/components/VoiceInput';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import AIGoalAssistant from '@/components/AIGoalAssistant';
import SmartGoalWizard from '@/components/SmartGoalWizard';
import confetti from 'canvas-confetti';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoalsPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors, designTemplate } = useTheme();
  const [goals, setGoals] = useState([]);
  const [stats, setStats] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [useWizard, setUseWizard] = useState(true); // NEW: Use wizard by default
  const [editingGoal, setEditingGoal] = useState(null);
  const [activeTab, setActiveTab] = useState('summer');
  
  // For AI Goal Assistant
  const [interests, setInterests] = useState([]);
  const [skills, setSkills] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    specific: '',
    measurable: '',
    achievable: '',
    relevant: '',
    deadline: '',
    goal_type: 'summer',
    visibility: 'private'
  });

  useEffect(() => {
    fetchGoals();
    fetchStats();
    fetchRequirements();
    fetchUserProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGoals = async () => {
    try {
      const res = await fetch(`${API_URL}/api/goals`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setGoals(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/goals/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const [interestsRes, skillsRes] = await Promise.all([
        fetch(`${API_URL}/api/profile/interests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/profile/skills`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (interestsRes.ok) {
        const data = await interestsRes.json();
        setInterests(data.interests || []);
      }
      
      if (skillsRes.ok) {
        const data = await skillsRes.json();
        setSkills(data.skills?.map(s => s.skill_id) || []);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchRequirements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/goals/requirements`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setRequirements(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      specific: '',
      measurable: '',
      achievable: '',
      relevant: '',
      deadline: '',
      goal_type: activeTab,
      visibility: 'private'
    });
    setEditingGoal(null);
  };

  const openCreateModal = (type = 'summer') => {
    resetForm();
    setFormData(prev => ({ ...prev, goal_type: type }));
    setShowCreateModal(true);
  };

  const openEditModal = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      specific: goal.specific || goal.description || '',
      measurable: goal.measurable || '',
      achievable: goal.achievable || '',
      relevant: goal.relevant || '',
      deadline: goal.deadline || '',
      goal_type: goal.goal_type || 'summer',
      visibility: goal.visibility || 'private'
    });
    setShowCreateModal(true);
  };

  const saveGoal = async () => {
    if (!formData.title || !formData.specific || !formData.deadline) {
      toast.error('Please fill in required fields (Title, Description, Deadline)');
      return;
    }

    const endpoint = editingGoal 
      ? `${API_URL}/api/goals/${editingGoal.id}`
      : `${API_URL}/api/goals`;
    
    const method = editingGoal ? 'PUT' : 'POST';

    try {
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        if (editingGoal) {
          toast.success('Goal updated!');
        } else {
          toast.success(`Goal created! +${data.points_earned} points 🎯`);
        }
        setShowCreateModal(false);
        resetForm();
        fetchGoals();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save goal');
      }
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const completeGoal = async (goalId) => {
    try {
      const res = await fetch(`${API_URL}/api/goals/${goalId}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        const data = await res.json();
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast.success(`Goal completed! +${data.points_earned} points 🎉`);
        if (data.streak_message) {
          setTimeout(() => toast.success(data.streak_message, { duration: 4000 }), 1000);
        }
        fetchGoals();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to complete goal');
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      const res = await fetch(`${API_URL}/api/goals/${goalId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        toast.success('Goal deleted');
        fetchGoals();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const addToGoogleCalendar = (goal) => {
    const startDate = new Date(goal.deadline);
    const endDate = new Date(goal.deadline);
    endDate.setHours(endDate.getHours() + 1);
    
    const formatDate = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, '');
    
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.set('action', 'TEMPLATE');
    url.searchParams.set('text', `🎯 Goal Deadline: ${goal.title}`);
    url.searchParams.set('details', `SMART Goal:\n\nSpecific: ${goal.specific}\nMeasurable: ${goal.measurable || 'N/A'}\nAchievable: ${goal.achievable || 'N/A'}\nRelevant: ${goal.relevant || 'N/A'}\n\nSet in YAS Connect`);
    url.searchParams.set('dates', `${formatDate(startDate)}/${formatDate(endDate)}`);
    url.searchParams.set('add', 'syep-reminder');
    
    window.open(url.toString(), '_blank');
    toast.success('Opening Google Calendar...');
  };

  const summerGoals = goals.filter(g => (g.goal_type || 'summer') === 'summer');
  const longTermGoals = goals.filter(g => g.goal_type === 'long_term');
  
  const activeSummerGoals = summerGoals.filter(g => g.status === 'active');
  const completedSummerGoals = summerGoals.filter(g => g.status === 'completed');
  const activeLongTermGoals = longTermGoals.filter(g => g.status === 'active');
  const completedLongTermGoals = longTermGoals.filter(g => g.status === 'completed');

  const getVisibilityIcon = (visibility) => {
    switch(visibility) {
      case 'public': return <Users className="w-4 h-4" />;
      case 'group': return <Eye className="w-4 h-4" />;
      default: return <Lock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors?.background || '#FEF9F3' }}>
      {/* Header */}
      <div className="relative overflow-hidden rounded-b-3xl" style={{ background: colors?.header_gradient || colors?.header || '#1B2D4F', color: '#fff' }}>
        <div className="relative p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-white/20 rounded-full"
                data-testid="back-button"
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-white tracking-tight">My Goals</h1>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>Track your progress</p>
              </div>
            </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-white/15 rounded-2xl p-4 backdrop-blur-xl border border-white/20">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.9)' }}>Short-term Goals</p>
                  <Badge className="bg-white/20 text-white">{stats.summer_goals.active}/{stats.summer_goals.max_allowed}</Badge>
                </div>
                <Progress 
                  value={(stats.summer_goals.total / stats.summer_goals.min_required) * 100} 
                  className="h-2 bg-white/30"
                />
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {stats.summer_goals.completed} completed • Min {stats.summer_goals.min_required} required
                </p>
              </div>
              <div className="bg-white/15 rounded-2xl p-4 backdrop-blur-xl border border-white/20">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.9)' }}>Long-term Goals</p>
                  <Badge className="bg-white/20 text-white">{stats.long_term_goals.active}/{stats.long_term_goals.max_allowed}</Badge>
                </div>
                <Progress 
                  value={(stats.long_term_goals.total / stats.long_term_goals.min_required) * 100} 
                  className="h-2 bg-white/30"
                />
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {stats.long_term_goals.completed} completed • Min {stats.long_term_goals.min_required} required
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Goal Nudge - Friendly, not blocking */}
      {requirements && !requirements.all_requirements_met && (
        <div className="max-w-4xl mx-auto px-4 -mt-2 mb-4">
          <div className="rounded-2xl p-4 bg-blue-50 border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-100">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-blue-800">
                  Set Your Goals to Start Earning Points
                </p>
                <p className="text-sm mt-1 text-blue-700">
                  {requirements.summer_goals.met 
                    ? <><strong>Short-term goal</strong> <span className="text-green-600">✓ Done!</span></>
                    : <><strong>Add 1 short-term goal</strong> to earn 25 points</>
                  }
                  {' • '}
                  {requirements.long_term_goals.met
                    ? <><strong>Long-term goal</strong> <span className="text-green-600">✓ Done!</span></>
                    : <><strong>Add 1 long-term goal</strong> to earn 25 points</>
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full bg-white rounded-2xl p-1 shadow-card border border-border/50">
            <TabsTrigger value="summer" className="flex-1 rounded-xl data-[state=active]:bg-[#E86C3D] data-[state=active]:text-white">
              <Star className="w-4 h-4 mr-2" /> Short-term Goals
            </TabsTrigger>
            <TabsTrigger value="long_term" className="flex-1 rounded-xl data-[state=active]:bg-primary data-[state=active]:text-white">
              <Target className="w-4 h-4 mr-2" /> Long-term Goals
            </TabsTrigger>
          </TabsList>

          {/* Short-term Goals Tab */}
          <TabsContent value="summer" className="space-y-4 mt-4">
            {/* Create Button */}
            {stats && stats.summer_goals.active < stats.summer_goals.max_allowed && (
              <Button
                onClick={() => openCreateModal('summer')}
                className="w-full h-14 rounded-2xl bg-orange-500 hover:bg-orange-600 text-lg shadow-lg"
                data-testid="create-summer-goal-button"
              >
                <Plus className="w-6 h-6 mr-2" />
                Add Short-term Goal
              </Button>
            )}

            {/* Minimum Goals Alert */}
            {stats && stats.summer_goals.total < stats.summer_goals.min_required && (
              <Card className="p-4 bg-amber-50 border-amber-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <Target className="w-6 h-6 text-amber-600 mt-1" />
                  <div>
                    <p className="font-semibold text-amber-900">Set at least {stats.summer_goals.min_required} short-term goals</p>
                    <p className="text-sm text-amber-700">
                      You have {stats.summer_goals.total} of {stats.summer_goals.min_required} minimum required.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Active Short-term Goals */}
            <GoalsList 
              title="Active Short-term Goals"
              goals={activeSummerGoals}
              onComplete={completeGoal}
              onEdit={openEditModal}
              onDelete={deleteGoal}
              onAddToCalendar={addToGoogleCalendar}
              getVisibilityIcon={getVisibilityIcon}
              emptyMessage="No active short-term goals. Create one above!"
            />

            {/* Completed Short-term Goals */}
            {completedSummerGoals.length > 0 && (
              <GoalsList 
                title="Completed Short-term Goals"
                goals={completedSummerGoals}
                isCompleted
                getVisibilityIcon={getVisibilityIcon}
              />
            )}
          </TabsContent>

          {/* Long-term Goals Tab */}
          <TabsContent value="long_term" className="space-y-4 mt-4">
            {/* Create Button */}
            {stats && stats.long_term_goals.active < stats.long_term_goals.max_allowed && (
              <Button
                onClick={() => openCreateModal('long_term')}
                className="w-full h-14 rounded-2xl bg-indigo-500 hover:bg-indigo-600 text-lg"
                data-testid="create-longterm-goal-button"
              >
                <Plus className="w-6 h-6 mr-2" />
                Add Long-term Goal
              </Button>
            )}

            {/* Minimum Goals Alert */}
            {stats && stats.long_term_goals.total < stats.long_term_goals.min_required && (
              <Card className="p-4 bg-indigo-50 border-indigo-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <Target className="w-6 h-6 text-indigo-600 mt-1" />
                  <div>
                    <p className="font-semibold text-indigo-900">Set at least {stats.long_term_goals.min_required} long-term goals</p>
                    <p className="text-sm text-indigo-700">
                      Think about your career, education, and life goals!
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Active Long-term Goals */}
            <GoalsList 
              title="Active Long-term Goals"
              goals={activeLongTermGoals}
              onComplete={completeGoal}
              onEdit={openEditModal}
              onDelete={deleteGoal}
              onAddToCalendar={addToGoogleCalendar}
              getVisibilityIcon={getVisibilityIcon}
              emptyMessage="No active long-term goals. Create one above!"
              isLongTerm
            />

            {/* Completed Long-term Goals */}
            {completedLongTermGoals.length > 0 && (
              <GoalsList 
                title="Completed Long-term Goals"
                goals={completedLongTermGoals}
                isCompleted
                isLongTerm
                getVisibilityIcon={getVisibilityIcon}
              />
            )}
          </TabsContent>

            {/* AI Goal Assistant */}
            <AIGoalAssistant
              interests={interests}
              skills={skills}
              onGoalCreated={(aiGoal) => {
                setFormData({
                  ...formData,
                  title: aiGoal.title,
                  specific: aiGoal.action_plan?.[0] || '',
                  measurable: aiGoal.success_metrics?.[0] || '',
                  achievable: 'Yes, with consistent effort',
                  relevant: 'Aligns with my short-term goals',
                  deadline: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                });
                toast.success('AI suggestion loaded! Review and customize below.');
              }}
            />

        </Tabs>
      </div>

      {/* Create/Edit Goal Modal */}
      <>
        {showCreateModal && useWizard && !editingGoal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
            <div onClick={(e) => e.stopPropagation()}>
              <SmartGoalWizard
                onComplete={(goal) => {
                  setShowCreateModal(false);
                  confetti({ particleCount: 100, spread: 70 });
                  toast.success('Goal created! +10 points');
                  fetchGoals();
                }}
                onCancel={() => setShowCreateModal(false)}
                interests={interests}
                skills={skills}
              />
            </div>
          </div>
        )}

        {showCreateModal && (!useWizard || editingGoal) && (
          <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) { if (window.confirm('Are you sure? Your progress will be lost.')) setShowCreateModal(false); } else { setShowCreateModal(open); } }}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] flex flex-col overflow-y-auto">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Target className={`w-6 h-6 ${formData.goal_type === 'long_term' ? 'text-indigo-500' : 'text-green-500'}`} />
              {editingGoal ? 'Edit Goal' : `Create ${formData.goal_type === 'long_term' ? 'Long-term' : 'Summer'} Goal`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div>
              <Label className="font-semibold">Goal Title *</Label>
              <Input
                placeholder="e.g., Save $500 this summer"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="rounded-xl mt-1"
                data-testid="goal-title-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Specific * (What exactly will you do?)</Label>
                <VoiceInput size="sm" onTranscript={(text) => setFormData(prev => ({...prev, specific: prev.specific ? prev.specific + ' ' + text : text}))} />
              </div>
              <Textarea
                placeholder="I will save $500 by putting $80 from each paycheck into my savings account"
                value={formData.specific}
                onChange={(e) => setFormData({...formData, specific: e.target.value})}
                className="rounded-xl mt-1 min-h-[60px]"
                data-testid="goal-specific-input"
              />
            </div>

            <div>
              <Label className="font-semibold">Measurable (How will you track progress?)</Label>
              <Textarea
                placeholder="I will check my savings balance weekly and log it"
                value={formData.measurable}
                onChange={(e) => setFormData({...formData, measurable: e.target.value})}
                className="rounded-xl mt-1 min-h-[60px]"
              />
            </div>

            <div>
              <Label className="font-semibold">Achievable (What steps will you take?)</Label>
              <Textarea
                placeholder="I will set up automatic transfers on payday"
                value={formData.achievable}
                onChange={(e) => setFormData({...formData, achievable: e.target.value})}
                className="rounded-xl mt-1 min-h-[60px]"
              />
            </div>

            <div>
              <Label className="font-semibold">Relevant (Why does this matter to you?)</Label>
              <Textarea
                placeholder="This will help me have money for college expenses"
                value={formData.relevant}
                onChange={(e) => setFormData({...formData, relevant: e.target.value})}
                className="rounded-xl mt-1 min-h-[60px]"
              />
            </div>

            <div>
              <Label className="font-semibold">Deadline * (When will you achieve this?)</Label>
              <Input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                className="rounded-xl mt-1"
                data-testid="goal-deadline-input"
              />
            </div>

            <div>
              <Label className="font-semibold">Who can see this goal?</Label>
              <Select 
                value={formData.visibility} 
                onValueChange={(v) => setFormData({...formData, visibility: v})}
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" /> Private (Only me, Lead Monitor & Parent)
                    </div>
                  </SelectItem>
                  <SelectItem value="group">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4" /> My Group (Share with my group)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                💡 When you achieve this goal, it will be shared on the Wins Wall for everyone to celebrate!
              </p>
            </div>
          </div>

          <div className="flex-shrink-0 pt-4 border-t mt-2">
            <Button
              onClick={saveGoal}
              className={`w-full h-12 rounded-full ${
                formData.goal_type === 'long_term' 
                  ? 'bg-indigo-500 hover:bg-indigo-600' 
                  : 'bg-green-500 hover:bg-green-600'
              }`}
              data-testid="submit-goal-button"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {editingGoal ? 'Save Changes' : `Create Goal (+10 points)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
        )}
      </>

      <Navigation role="youth" />
    </div>
  );
};

// Goals List Component
const GoalsList = ({ 
  title, goals, onComplete, onEdit, onDelete, onAddToCalendar, 
  getVisibilityIcon, emptyMessage, isCompleted, isLongTerm 
}) => {
  const getDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    const deadlineDate = new Date(deadline);
    if (isNaN(deadlineDate.getTime())) return null;
    const diffTime = deadlineDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div>
      <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
        {isCompleted ? (
          <Trophy className="w-5 h-5 text-yellow-500" />
        ) : (
          <Target className={`w-5 h-5 ${isLongTerm ? 'text-indigo-500' : 'text-green-500'}`} />
        )}
        {title}
      </h2>

      {goals.length === 0 ? (
        <Card className="p-8 text-center rounded-3xl">
          <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{emptyMessage || 'No goals yet'}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {goals.map((goal) => {
            const daysRemaining = getDaysRemaining(goal.deadline);
            
            return (
              <motion.div
                key={goal.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className={`p-5 rounded-3xl ${isCompleted ? 'bg-green-50 border-green-200' : ''}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-lg">{goal.title}</h3>
                        <Badge variant="outline" className="flex items-center gap-1">
                          {getVisibilityIcon(goal.visibility)}
                          {goal.visibility || 'private'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {goal.deadline ? `Due: ${new Date(goal.deadline).toLocaleDateString()}` : 'No deadline set'}
                        </span>
                        {!isCompleted && daysRemaining !== null && (
                          <Badge className={
                            daysRemaining < 0 ? 'bg-red-100 text-red-800' :
                            daysRemaining <= 3 ? 'bg-orange-100 text-orange-800' :
                            daysRemaining <= 7 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }>
                            {daysRemaining < 0 
                              ? `${Math.abs(daysRemaining)}d overdue` 
                              : daysRemaining === 0 
                                ? 'Due today!' 
                                : `${daysRemaining}d left`
                            }
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isCompleted && (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm space-y-2">
                    {(goal.specific || goal.description) && (
                      <p><strong>{goal.specific ? 'Specific' : 'Description'}:</strong> {goal.specific || goal.description}</p>
                    )}
                    {goal.measurable && <p><strong>Measurable:</strong> {goal.measurable}</p>}
                    {goal.achievable && <p><strong>Achievable:</strong> {goal.achievable}</p>}
                    {goal.relevant && <p><strong>Relevant:</strong> {goal.relevant}</p>}
                  </div>

                  {!isCompleted && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => onComplete(goal.id)}
                        className="flex-1 rounded-xl bg-green-500 hover:bg-green-600"
                        data-testid={`complete-goal-${goal.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Goal Achieved! ✓
                      </Button>
                      {goal.deadline && (
                        <Button
                          variant="outline"
                          onClick={() => onAddToCalendar(goal)}
                          className="rounded-xl"
                          title="Add to Google Calendar"
                        >
                          <CalendarPlus className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => onEdit(goal)}
                        className="rounded-xl"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => onDelete(goal.id)}
                        className="rounded-xl text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  {isCompleted && goal.completed_at && (
                    <p className="text-sm text-green-700">
                      Completed on {new Date(goal.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalsPage;
